import { useDispatch, useSelector } from "react-redux";
import CustomPage from "components/CustomPage";
import { useMemo, useRef, useState } from "react";
import DatesReports from "components/CustomSearch";
import moment from "moment";
import NullCheck from "components/NullCheck";
import http from "../../../../http";
import { DATE_FOR_API_FORMAT, MC_DEVICE_TYPE } from "config/const";
import { setShowLoading, setShowMassage } from "store/sysSlice";
import ChartHoursAvg from "./ChartHoursAvg";
import CustomDataGrid from "components/customDataGrid";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import CustomCard from "components/CustomCard";
import { getIconByDevice } from "containers/controlAndMonitoring/const";
import { findKeyByValue } from "config/functions";
import { CalendarMonth, FmdGoodOutlined } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { temperatureReportControllers, temperatureReportRequest } from 'containers/microControllers/b22/temperatureReports';

const McTempReport = () => {
  const dispatch = useDispatch();
  const { bt } = useBizTypeTranslation();
  const mcList = useSelector((state) => state?.mc.mcList);
  const user = useSelector((state) => state.userSettings?.user);
  const reportControllers = useMemo(() => temperatureReportControllers(mcList, user), [mcList, user]);
  const [datesReports, setDatesReports] = useState(true);
  const [list, setList] = useState([]);
  const [data, setData] = useState(null);
  const [gridDataList, setGridDataList] = useState([]);
  const latestRequest = useRef(0);

  const callBackDate = async (selection) => {
    const controller = reportControllers.find(item => item.guid === selection?.selectedMc);
    if (!controller) {
      dispatch(setShowLoading(false));
      dispatch(setShowMassage({ massage: "SELECT_MC_ERROR", type: "error" }));
      setTimeout(() => setDatesReports(true), 100);
      return
    }
    dispatch(setShowLoading(true));
    const requestId = ++latestRequest.current;
    setList([]); setData(null); setGridDataList([]);
    const dates = {
      start: moment(selection.startDate).startOf('day'),
      end: moment(selection.endDate).endOf('day'),
    };
    const request = temperatureReportRequest(controller, dates.start, dates.end);
    try {
      const response = request.config ? await http.get(request.url, request.config) : await http.get(request.url);
      if (requestId !== latestRequest.current) return;
      if (response?.status !== 200) throw new Error('Report request failed');
      const rows = response.data.data || [];
      setList(rows);
      setData({ dates, list: rows, mcData: controller });
    } catch {
      if (requestId === latestRequest.current) dispatch(setShowMassage({ massage: 'B22_LOAD_FAILED', type: 'error' }));
    } finally { if (requestId === latestRequest.current) dispatch(setShowLoading(false)); }
  };

  function getDateRanges(startDate, endDate, type = "day") {

    const start = moment(startDate).startOf("day");
    const end = moment(endDate).startOf("day");

    const ranges = [];

    if (start.isAfter(end)) return [];

    if (type === "day") {
      let current = start.clone();

      while (current.isSameOrBefore(end, "day")) {
        ranges.push({ startDate: current.format(DATE_FOR_API_FORMAT), endDate: current.format(DATE_FOR_API_FORMAT) });
        current.add(1, "day");
      }
    }

    else if (type === "week") {
      let current = start.clone().startOf("week");

      while (current.isSameOrBefore(end, "day")) {
        const rangeStart = moment.max(current.clone(), start.clone());
        const rangeEnd = moment.min(current.clone().endOf("week"), end.clone());
        ranges.push({ startDate: rangeStart, endDate: rangeEnd });
        current.add(1, "week");
      }
    }

    else if (type === "month") {
      let current = start.clone().startOf("month");

      while (current.isSameOrBefore(end, "day")) {
        const rangeStart = moment.max(current.clone(), start.clone());
        const rangeEnd = moment.min(current.clone().endOf("month"), end.clone());
        ranges.push({ startDate: rangeStart, endDate: rangeEnd });
        current.add(1, "month");
      }
    }

    return ranges;
  }

  return (
    <CustomPage hideTitle >
      <Stack spacing={2} mb={2}>

        <CustomCard width="100%" direction="row" pb={1} padding={1} alignItems="center" justifyContent="space-between">

          <Stack direction={{ xs: "column", md: "row" }} justifyContent='space-between'>
            <Stack spacing={1}>
              <Typography color="primary.text">
                {data?.mcData?.name}
              </Typography>

              <Stack direction="row" spacing={{ xs: 1, md: 2 }}>
                <NullCheck valueForCheck={data?.mcData?.deviceType !== MC_DEVICE_TYPE.NONE}>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    {getIconByDevice(data?.mcData?.deviceType)}
                    <Typography variant="body2" color="primary.text">
                      {bt(findKeyByValue(MC_DEVICE_TYPE, data?.mcData?.deviceType))}
                    </Typography>
                  </Stack>
                </NullCheck>

                <NullCheck valueForCheck={data?.mcData?.area?.name}>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    <FmdGoodOutlined fontSize="small" sx={{ color: "primary.main" }} />
                    <Typography variant="body2" color="primary.text">
                      {data?.mcData?.area?.name}
                    </Typography>
                  </Stack>
                </NullCheck>
              </Stack>
            </Stack>
            <Stack />
          </Stack>

          <IconButton onClick={() => setDatesReports(true)}>
            <CalendarMonth />
          </IconButton>
        </CustomCard>

        <NullCheck valueForCheck={list?.length > 0} noData='NO_DATA'>
          <CustomCard spacing={0} padding={0}>

            <ChartHoursAvg padding={0} data={data} getDateRanges={getDateRanges} setGridDataList={setGridDataList} />

            <Stack padding={1} direction={{ xs: "column-reverse", md: "row" }} justifyContent="space-between" spacing={2}>

              <CustomCard width="100%" height="100%">
                <Typography variant="h6" textAlign="center">{bt('ALL_DATA')}</Typography>
                <CustomDataGrid
                  pageSize={12}
                  getRowClassName={(params) => { return params?.row?.isAlert ? "hot" : "" }}
                  columns={[
                    {
                      field: "time",
                      headerName: bt("TIME"),
                      width: 150,
                      filterable: false,
                      renderCell: (value) => value?.row?.time,
                    },
                    {
                      field: "tempInPlace",
                      headerName: bt("TEMP_IN_PLACE"),
                      flex: 1,
                      renderCell: (value) => `${value?.row?.tempInPlace}°`,
                    },
                  ]
                  }
                  list={gridDataList?.map((item, index) => ({ id: index, ...item, tempInPlace: item?.temp }))}
                />
              </CustomCard>

              <CustomCard width="100%" height="100%">
                <Typography variant="h6" textAlign="center">{bt('ALERTS')}</Typography>
                <CustomDataGrid
                  pageSize={12}
                  columns={[
                    {
                      field: "time",
                      headerName: bt("TIME"),
                      width: 150,
                      filterable: false,
                      renderCell: (value) => value?.row?.time,
                    },
                    {
                      field: "tempInPlace",
                      headerName: bt("TEMP_IN_PLACE"),
                      flex: 1,
                      renderCell: (value) => `${value?.row?.tempInPlace}°`,
                    },
                  ]
                  }
                  list={gridDataList
                    ?.filter(item => item?.isAlert)
                    ?.map((item, index) => ({ id: index, ...item, tempInPlace: item?.temp })
                    )}
                />
              </CustomCard>

            </Stack>
          </CustomCard>

        </NullCheck>
      </Stack>


      <DatesReports
        btnTitle="CREATE_REPORT"
        title='DATES'
        mcList={reportControllers
          ?.map((item) => ({ guid: item?.guid, name: item?.name }))
        }
        open={datesReports}
        setOpen={setDatesReports}
        callBack={callBackDate}
      />
    </CustomPage>
  );
};
export default McTempReport;
